import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { registerQuery, useDataFetchingClient, useQuery, useMutation, registerMutation } from 'data-fetching-client';
import noAuthApiClient from 'hub-http/clients/noAuthApiClient';
import { userProfilePath } from '../../consts';
import { useDebouncedValue } from '../../utils/hooks';
import { unwrapHubHttpError } from '../../utils/api';
import noPortalClient from 'hub-http/clients/noPortalApiClient';
import { produce } from 'immer';
import { useUserInfo } from '../auth/authQueries';

const addAvatarLastUpdated = user => {
  return Object.assign({}, user, {
    avatarLastUpdated: Date.now()
  });
};

const GET_USERS_SEARCH = registerQuery({
  fieldName: 'users',
  args: ['count', 'offset', 'sortProperty', 'sortOrder', 'goals', 'products', 'goalSet', 'industryId', 'companyId', 'geolocationId', 'query'],

  fetcher(_ref) {
    let {
      goals,
      products
    } = _ref,
        restQueryParams = _objectWithoutPropertiesLoose(_ref, ["goals", "products"]);

    return noAuthApiClient.get(`${userProfilePath}/search`, {
      query: Object.assign({}, restQueryParams, {}, goals && goals.length && {
        goals: goals.join(',')
      }, {}, products && products.length && {
        products: products.join(',')
      })
    });
  }

});
export const GET_USER_BY_ID = registerQuery({
  fieldName: 'userById',
  args: ['userId'],

  fetcher({
    userId
  }) {
    return noAuthApiClient.get(`${userProfilePath}/user-id/${userId}`).then(response => addAvatarLastUpdated(response));
  }

});
export const GET_USER_BY_USERNAME = registerQuery({
  fieldName: 'userByUsername',
  args: ['username'],

  fetcher({
    username
  }) {
    return noAuthApiClient.get(`${userProfilePath}/username/${username}`).then(response => addAvatarLastUpdated(response));
  }

});
export const useUsersSearch = (_ref2) => {
  let {
    page = 1,
    resultsPerPage = 50
  } = _ref2,
      queryParams = _objectWithoutPropertiesLoose(_ref2, ["page", "resultsPerPage"]);

  const client = useDataFetchingClient();
  const {
    data,
    error: usersError,
    loading: usersLoading
  } = useQuery(GET_USERS_SEARCH, {
    variables: Object.assign({
      count: resultsPerPage,
      offset: (page - 1) * resultsPerPage
    }, queryParams),
    onCompleted: queryData => {
      if (queryData && queryData.users && queryData.users.results) {
        queryData.users.results.forEach(user => {
          client.cache.writeQuery({
            query: GET_USER_BY_ID,
            data: {
              userById: user
            },
            variables: {
              userId: user.userId
            }
          });
          client.cache.writeQuery({
            query: GET_USER_BY_USERNAME,
            data: {
              userByUsername: user
            },
            variables: {
              username: user.userName
            }
          });
        });
      }
    }
  });
  return {
    users: data ? data.users.results : [],
    totalUsers: data ? data.users.total : 0,
    totalPages: data ? Math.ceil(data.users.total / resultsPerPage) : 0,
    usersError,
    usersLoading
  };
};
export const useUserById = userId => {
  const client = useDataFetchingClient();
  const {
    data,
    error: userError,
    loading: userLoading
  } = useQuery(GET_USER_BY_ID, {
    variables: {
      userId
    },
    skip: !userId,
    onCompleted: queryData => {
      client.cache.writeQuery({
        query: GET_USER_BY_USERNAME,
        data: {
          userByUsername: queryData.userById
        },
        variables: {
          username: queryData.userById.userName
        }
      });
    }
  });
  const userData = data ? data.userById : null;
  return {
    user: userData,
    userError,
    userLoading
  };
};
export const useUserByUsername = username => {
  const client = useDataFetchingClient();
  const {
    data,
    error: userError,
    loading: userLoading
  } = useQuery(GET_USER_BY_USERNAME, {
    variables: {
      username
    },
    skip: !username,
    onCompleted: queryData => {
      client.cache.writeQuery({
        query: GET_USER_BY_ID,
        data: {
          userById: queryData.userByUsername
        },
        variables: {
          userId: queryData.userByUsername.userId
        }
      });
    }
  });
  const userData = data ? data.userByUsername : null;
  return {
    user: userData,
    userError,
    userLoading
  };
};
export const useUserByAuth = () => {
  const {
    userInfo
  } = useUserInfo();
  const userId = userInfo ? userInfo.user_id : null;
  return useUserById(userId);
};
export const CLAIM_PROFILE = registerMutation({
  fieldName: 'claimProfile',
  args: ['userId'],

  fetcher({
    userId
  }) {
    return noPortalClient.get(`${userProfilePath}/${userId}?hasConsented=true`);
  }

});
export const useClaimProfile = () => {
  const {
    userError,
    userLoading
  } = useUserByAuth();
  const [claimProfile] = useMutation(CLAIM_PROFILE, {
    update(cache, {
      data: user
    }) {
      if (user) {
        cache.writeQuery({
          query: GET_USER_BY_USERNAME,
          data: {
            userByUsername: user.claimProfile
          },
          variables: {
            username: user.claimProfile.userName
          }
        });
        cache.writeQuery({
          query: GET_USER_BY_ID,
          data: {
            userById: user.claimProfile
          },
          variables: {
            userId: user.claimProfile.userId
          }
        });
      }
    }

  });
  const httpError = unwrapHubHttpError(userError);
  let hasClaimedProfile = false;
  let fetchClaimError = null;

  if (httpError && httpError.status === 404) {
    hasClaimedProfile = false;
  } else if (httpError) {
    hasClaimedProfile = false;
    fetchClaimError = httpError;
  } else {
    hasClaimedProfile = true;
  }

  return {
    hasClaimedProfile,
    claimProfile,
    fetchClaimError,
    claimLoading: userLoading
  };
};
export const useUsernameIsAvailable = username => {
  const currentUser = useUserByAuth();
  const debouncedUsername = useDebouncedValue(username);
  const {
    user,
    userError,
    userLoading
  } = useUserByUsername(debouncedUsername || null);
  const hubHttpError = unwrapHubHttpError(userError);

  if (hubHttpError && hubHttpError.status === 404) {
    return 'available';
  } else if (userLoading || user && currentUser && currentUser.user && user.userId === currentUser.user.userId) {
    return 'current';
  } else {
    return 'unavailable';
  }
};
export const UPDATE_USER_BY_USERNAME = registerMutation({
  fieldName: 'updateUserByUsername',
  args: ['user', 'userName'],

  fetcher({
    user,
    userName
  }) {
    const {
      industry,
      geolocation,
      company
    } = user,
          restOfUser = _objectWithoutPropertiesLoose(user, ["industry", "geolocation", "company"]);

    return noPortalClient.put(`${userProfilePath}/${userName}`, {
      data: produce(restOfUser, draft => {
        if (industry) {
          draft.industryId = industry.id;
        }

        if (geolocation) {
          draft.geolocationId = geolocation.id;
        }

        if (company) {
          draft.companyId = company.id;
        }
      })
    });
  }

});
export const useUpdateUser = () => {
  const [updateUser, {
    loading: userUpdateLoading,
    data
  }] = useMutation(UPDATE_USER_BY_USERNAME, {
    update(cache, {
      data: user
    }) {
      if (user) {
        cache.writeQuery({
          query: GET_USER_BY_USERNAME,
          data: {
            userByUsername: user.updateUserByUsername
          },
          variables: {
            username: user.updateUserByUsername.userName
          }
        });
        cache.writeQuery({
          query: GET_USER_BY_ID,
          data: {
            userById: user.updateUserByUsername
          },
          variables: {
            userId: user.updateUserByUsername.userId
          }
        });
      }
    }

  });
  return {
    updateUser,
    userUpdateLoading,
    updatedUser: data ? data.updateUserByUsername : null
  };
};
export const GET_USER_COMMUNITY_STATS_BY_ID = registerQuery({
  fieldName: 'userCommunityStatsById',
  args: ['userId'],

  fetcher() {
    return new Promise(resolve => {
      setTimeout(() => {
        resolve({
          acceptedSolutions: Math.floor(Math.random() * 50),
          replies: Math.floor(Math.random() * 50),
          kudosGiven: Math.floor(Math.random() * 50),
          kudosReceived: Math.floor(Math.random() * 50),
          ideas: Math.floor(Math.random() * 50),
          numberLogins: Math.floor(Math.random() * 50),
          threads: Math.floor(Math.random() * 50)
        });
      }, 200);
    });
  }

});
export const useCommunityStatsById = userId => {
  const {
    data,
    error: communityStatsError,
    loading: communityStatsLoading
  } = useQuery(GET_USER_COMMUNITY_STATS_BY_ID, {
    variables: {
      userId
    }
  });
  const communityStatsData = data ? data.userCommunityStatsById : null;
  return {
    communityStats: communityStatsData,
    communityStatsError,
    communityStatsLoading
  };
};
export const useIsCurrentUser = userId => {
  const {
    userInfo: authedUser
  } = useUserInfo();
  if (!authedUser || !authedUser.user_id || !userId) return false;
  return authedUser.user_id === userId;
};